import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebase";
import "./styles/EmailCapture.css";

type Props = {
  list: string; // e.g. "fl-solar-alerts"
  source: string; // e.g. "blog:some-slug", "calculator"
  title: string;
  blurb: string;
  buttonLabel?: string;
};

const CONSENT_TEXT =
  "Occasional emails from Energy Guru. No spam, unsubscribe anytime.";

export default function EmailCapture({
  list,
  source,
  title,
  blurb,
  buttonLabel = "Keep me posted",
}: Props) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot — humans never see it
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">("idle");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      return;
    }
    if (company) {
      setStatus("done");
      return;
    }
    setStatus("saving");
    try {
      await addDoc(collection(db, "emailSubscribers"), {
        email: email.trim().toLowerCase(),
        site: "energy-guru",
        list,
        source,
        consentText: CONSENT_TEXT,
        createdAt: serverTimestamp(),
      });
      setStatus("done");
    } catch (err) {
      console.error("Email signup failed:", err);
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="email-capture email-capture-done">
        <p className="ec-title">You're on the list ✓</p>
        <p className="ec-blurb">We'll email you when something actually changes your solar math.</p>
      </div>
    );
  }

  return (
    <div className="email-capture">
      <p className="ec-title">{title}</p>
      <p className="ec-blurb">{blurb}</p>
      <form className="ec-form" onSubmit={submit}>
        <input
          type="text"
          className="ec-honeypot"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
        <input
          type="email"
          className="ec-email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button type="submit" className="ec-btn" disabled={status === "saving"}>
          {status === "saving" ? "Saving…" : buttonLabel}
        </button>
      </form>
      {status === "error" && (
        <p className="ec-error">Please enter a valid email and try again.</p>
      )}
      <p className="ec-consent">{CONSENT_TEXT}</p>
    </div>
  );
}
